import React from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckIcon from '@mui/icons-material/Check';

import SectionAccordion from './SectionAccordion';
import DayTimeslotCard from './DayTimeslotCard';
import SwitchInput from '../../../../../components/SwitchInput';
import { THEME_COLOR, TEXT_SECONDARY_COLOR, THEME_COLOR4 } from '../../../../../constants';
import { DAYS_OF_WEEK } from '../schemas/menu-item-schema';
import type { TMenuItemFormValues, TAvailabilitySlot, TAvailabilityTiming } from '../schemas/menu-item-schema';
import { createDefaultTiming, createEmptyTiming } from '../utils/menu-item-utils';

const DAY_LABELS: Record<(typeof DAYS_OF_WEEK)[number], string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

type TAvailabilitySectionProps = {
  accordionRef: React.RefObject<HTMLElement>;
  expanded?: boolean;
  iconColor: string;
  headerBackground: string;
  onToggle?: (expanded: boolean) => void;
};

const AvailabilitySection = ({ accordionRef, expanded, onToggle, iconColor, headerBackground }: TAvailabilitySectionProps) => {
  const { watch, setValue, formState, getFieldState } =
    useFormContext<TMenuItemFormValues>();

  const hasError = formState.isSubmitted && !!formState.errors.availabilitySlots;

  const restrictAvailability = watch('restrictAvailability');
  const availabilitySlots = watch('availabilitySlots') ?? [];
  const isSubmitted = formState.isSubmitted;

  const update = (updatedSlots: TAvailabilitySlot[]) => {
    setValue('availabilitySlots', updatedSlots, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: isSubmitted,
    });
  };

  const getSlot = (day: (typeof DAYS_OF_WEEK)[number]) =>
    availabilitySlots.find((s) => s.day === day);

  const toggleDay = (day: (typeof DAYS_OF_WEEK)[number], enabled: boolean) => {
    update(
      availabilitySlots.map((slot) => {
        if (slot.day !== day) return slot;
        const currentTimings = slot.timings ?? [];
        return {
          ...slot,
          enabled,
          timings: enabled
            ? currentTimings.length > 0
              ? currentTimings
              : [createDefaultTiming()]
            : currentTimings.filter((t) => t.from && t.to),
        };
      })
    );
  };

  const updateTiming = (
    day: (typeof DAYS_OF_WEEK)[number],
    index: number,
    patch: Partial<TAvailabilityTiming>
  ) => {
    update(
      availabilitySlots.map((slot) => {
        if (slot.day !== day) return slot;
        return {
          ...slot,
          timings: (slot.timings ?? []).map((t, i) =>
            i === index ? { ...t, ...patch } : t
          ),
        };
      })
    );
  };

  const addTiming = (day: (typeof DAYS_OF_WEEK)[number]) => {
    update(
      availabilitySlots.map((slot) => {
        if (slot.day !== day) return slot;
        return {
          ...slot,
          enabled: true,
          timings: [...(slot.timings ?? []), createEmptyTiming()],
        };
      })
    );
  };

  const removeTiming = (day: (typeof DAYS_OF_WEEK)[number], index: number) => {
    update(
      availabilitySlots.map((slot) => {
        if (slot.day !== day) return slot;
        const timings = slot.timings ?? [];
        if (slot.enabled && timings.length <= 1) return slot;
        return { ...slot, timings: timings.filter((_, i) => i !== index) };
      })
    );
  };

  const applyPreset = (days: readonly (typeof DAYS_OF_WEEK)[number][]) => {
    update(
      availabilitySlots.map((slot) => {
        if (days.includes(slot.day)) {
          return {
            ...slot,
            enabled: true,
            timings: [createDefaultTiming()],
          };
        }
        return { ...slot, enabled: false };
      })
    );
  };

  const getDayError = (slotIndex: number, enabled: boolean) => {
    if (!enabled) return undefined;
    return getFieldState(
      `availabilitySlots.${slotIndex}.timings` as const,
      formState
    ).error?.message;
  };

  const getTimingError = (slotIndex: number, timingIndex: number) => ({
    from: getFieldState(
      `availabilitySlots.${slotIndex}.timings.${timingIndex}.from` as const,
      formState
    ).error?.message,
    to: getFieldState(
      `availabilitySlots.${slotIndex}.timings.${timingIndex}.to` as const,
      formState
    ).error?.message,
  });

  return (
    <SectionAccordion
      accordionRef={accordionRef}
      title="Availability"
      subtitle="When this item is available"
      icon={<AccessTimeOutlinedIcon fontSize="small" />}
      iconColor={iconColor}
      headerBackground={headerBackground}
      hasError={hasError}
      expanded={expanded}
      onToggle={onToggle}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Restrict toggle */}
        <Box sx={s.toggleCard}>
          <Box>
            <Typography sx={s.toggleTitle}>Restrict to specific days & times</Typography>
            <Typography sx={s.toggleSubtitle}>When off, this item is always available</Typography>
          </Box>
          <SwitchInput
            checked={!!restrictAvailability}
            onChange={() =>
              setValue('restrictAvailability', !restrictAvailability, {
                shouldDirty: true,
                shouldTouch: true,
              })
            }
          />
        </Box>

        {restrictAvailability && (
          <>
            {/* Preset buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <PresetBtn
                label="Everyday"
                onClick={() => applyPreset(DAYS_OF_WEEK)}
              />
              <PresetBtn
                label="Weekdays"
                onClick={() =>
                  applyPreset(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])
                }
              />
              <PresetBtn
                label="Weekends"
                onClick={() => applyPreset(['saturday', 'sunday'])}
              />
            </Box>

            {/* Day cards */}
            <Box>
              {DAYS_OF_WEEK.map((day, slotIndex) => {
                const slot = getSlot(day);
                return (
                  <DayTimeslotCard
                    key={day}
                    label={DAY_LABELS[day]}
                    enabled={slot?.enabled ?? false}
                    timings={slot?.timings ?? []}
                    onToggle={(enabled) => toggleDay(day, enabled)}
                    onTimingChange={(index, patch) => updateTiming(day, index, patch)}
                    onTimingAdd={() => addTiming(day)}
                    onTimingRemove={(index) => removeTiming(day, index)}
                    dayError={getDayError(slotIndex, slot?.enabled ?? false)}
                    timingErrors={(slot?.timings ?? []).map((_, timingIndex) =>
                      getTimingError(slotIndex, timingIndex)
                    )}
                  />
                );
              })}
            </Box>

            <Typography sx={s.helpText}>
              Toggle a day off to hide this item on that day. Add multiple time slots for separate
              windows.
            </Typography>
          </>
        )}
      </Box>
    </SectionAccordion>
  );
};

const PresetBtn = ({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.75,
      borderRadius: '999px',
      border: `1px solid ${THEME_COLOR4}`,
      backgroundColor: '#FFF',
      cursor: 'pointer',
      font: 'inherit',
      appearance: 'none',
      transition: 'border-color 0.15s, background-color 0.15s',
      '&:hover': {
        borderColor: THEME_COLOR,
        backgroundColor: `${THEME_COLOR}18`,
      },
    }}
  >
    {icon}
    <Typography sx={{ fontFamily: 'GilroySemiBold', fontSize: 12, color: '#000' }}>
      {label}
    </Typography>
  </Box>
);

export default AvailabilitySection;

const s = {
  toggleCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleTitle: { fontFamily: 'GilroySemiBold', fontSize: 13, color: '#000' },
  toggleSubtitle: { fontFamily: 'GilroyRegular', fontSize: 11, color: TEXT_SECONDARY_COLOR, mt: 0.25 },
  helpText: { fontFamily: 'GilroyRegular', fontSize: 12, color: TEXT_SECONDARY_COLOR },
};
